import React, { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { services } from "@/data/services";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const initialForm = {
  name: "", company: "", email: "", phone: "",
  pickup_location: "", dropoff_location: "",
  service_type: services[0].title,
  cargo_type: "", weight: "", pickup_date: "", notes: "",
};

export default function Quote() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.pickup_location || !form.dropoff_location) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setSubmitted(true);
      toast.success("Quote request received. We'll be in touch shortly!");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quote-page" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <Toaster position="top-right" richColors />
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">
            Free Estimate
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter">
            Request a Quote.
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Tell us about your shipment and we'll get back to you with pricing
            and pickup details. Response within one business day.
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            className="mt-12 border border-slate-200 bg-white p-10 lg:p-14 text-center"
            data-testid="quote-success"
          >
            <CheckCircle2 className="h-14 w-14 text-primary-brand mx-auto" />
            <h2 className="font-heading text-3xl font-black mt-6">Quote Received</h2>
            <p className="text-slate-600 mt-3 max-w-md mx-auto">
              Thanks for reaching out. A dispatcher will contact you within one
              business day to confirm details and pricing.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              data-testid="quote-submit-another"
              className="mt-8 inline-flex items-center gap-2 border border-primary-brand text-primary-brand px-6 py-3 uppercase tracking-wider text-sm font-bold hover:bg-primary-brand hover:text-white transition-colors"
            >
              Submit another <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200"
          >
            <Field label="Full name *" testid="q-name">
              <input required value={form.name} onChange={update("name")} data-testid="quote-input-name" className={inputCls} />
            </Field>
            <Field label="Company" testid="q-company">
              <input value={form.company} onChange={update("company")} data-testid="quote-input-company" className={inputCls} />
            </Field>
            <Field label="Email *" testid="q-email">
              <input type="email" required value={form.email} onChange={update("email")} data-testid="quote-input-email" className={inputCls} />
            </Field>
            <Field label="Phone *" testid="q-phone">
              <input required value={form.phone} onChange={update("phone")} data-testid="quote-input-phone" className={inputCls} />
            </Field>
            <Field label="Pickup location *" testid="q-pickup">
              <input required value={form.pickup_location} onChange={update("pickup_location")} data-testid="quote-input-pickup" className={inputCls} />
            </Field>
            <Field label="Drop-off location *" testid="q-dropoff">
              <input required value={form.dropoff_location} onChange={update("dropoff_location")} data-testid="quote-input-dropoff" className={inputCls} />
            </Field>
            <Field label="Service type" testid="q-service">
              <select
                value={form.service_type}
                onChange={update("service_type")}
                data-testid="quote-input-service"
                className={inputCls}
              >
                {services.map((s) => (
                  <option key={s.title} value={s.title} className="bg-brand-bg">{s.title}</option>
                ))}
              </select>
            </Field>
            <Field label="Preferred pickup date" testid="q-date">
              <input type="date" value={form.pickup_date} onChange={update("pickup_date")} data-testid="quote-input-date" className={inputCls} />
            </Field>
            <Field label="Cargo type" testid="q-cargo">
              <input value={form.cargo_type} onChange={update("cargo_type")} placeholder="e.g. furniture, pallets" data-testid="quote-input-cargo" className={inputCls} />
            </Field>
            <Field label="Weight / size" testid="q-weight">
              <input value={form.weight} onChange={update("weight")} placeholder="e.g. 2 pallets / 800 lbs" data-testid="quote-input-weight" className={inputCls} />
            </Field>
            <Field label="Additional notes" testid="q-notes" full>
              <textarea rows={4} value={form.notes} onChange={update("notes")} data-testid="quote-input-notes" className={inputCls + " resize-none"} />
            </Field>

            <div className="md:col-span-2 bg-brand-bg p-6 lg:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-slate-500">
                We respond within one business day. No spam, ever.
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit-btn"
                className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                {submitting ? "Submitting…" : "Send Quote Request"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors";

function Field({ label, children, full }) {
  return (
    <label className={`bg-brand-bg p-6 lg:p-8 block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">{label}</div>
      {children}
    </label>
  );
}
